import React, { useMemo } from "react"
import {
  get,
  pick,
  head,
  tail,
  chain,
  nth,
  orderBy,
  drop,
  fill,
  set,
  map,
  isEmpty,
  isFunction,
  reject,
  filter,
  chunk,
  cloneDeep,
  forEach,
  take,
  takeRight,
} from "lodash"
import ClassNames from "classnames"

import { infoTableColumns, tableColumns } from "config/table_columns"
import { BaseTable, InfoTable } from "components/molecules/table"
import { APIResponse } from "redux/apis/types"
import { useSettledAccountReport } from "components/actions/symbolDetail"
import * as Formatter from "utils/Formatter"
import { Labels } from "components/atoms/input/input"
import {
  PerformanceCompareBar,
  PerformanceHistoryBar,
} from "components/atoms/chart/barChart"
import { checkIsSp, judgingDomesticEquity } from "utils/util"
import { SpDetailTable } from "components/molecules/table/spTable"
import { SECURITY_TYPE } from "config/const"
import { NewsOne } from "components/organisms/modals/symbolDetail/news"
import { useSelector } from "react-redux"

type TableListType = Array<
  Array<{
    name: string
    table: string
  }>
>

type PerformanceProps = {
  symbolDetail: APIResponse.Res
}

type HistoryTableInfo = Array<{
  title: string | Function
  cls?: string
  component: Function
}>

type HistoryTableProps = {
  info: HistoryTableInfo
  data: APIResponse.Res
  cls?: string
  symbolDetail?: APIResponse.Res
}

type SettlementProps = {
  type: number
  setType: Function
  settlement: APIResponse.Res
  isSp: boolean
  symbolDetail: APIResponse.Res
}

type CashFlowProps = {
  flow: APIResponse.Res
  isSp: boolean
}

type QuarterProps = {
  type: number
  setType: Function
  data: APIResponse.Res
  isSp: boolean
}

type ReitProps = {
  type: number
  setType: Function
  data: APIResponse.Res
  isSp: boolean
}

type DividendProps = {
  history: APIResponse.Res
  dividend: APIResponse.Res
  isSp: boolean
  symbolDetail: APIResponse.Res
}

type IndexesProps = {
  indexes: APIResponse.Res
  isSp: boolean
  symbolDetail?: APIResponse.Res
}

const tableList: TableListType = [
  [
    { name: "割安性", table: "inexpensive" },
    { name: "安定性", table: "safety" },
  ],
  [
    { name: "収益性", table: "profitability" },
    { name: "成長性", table: "growth" },
  ],
]

const HistoryTable: React.FC<HistoryTableProps> = props => {
  const headerInfo = head(props.info)
  const bodyInfo = tail(props.info)
  return (
    <table className={ClassNames("table", props.cls)}>
      <thead>
        <tr>
          <th className="title-cell">{headerInfo.title}</th>
          {props.data.map((d: APIResponse.Res, i: number) => {
            return <th key={i}>{headerInfo.component(d)}</th>
          })}
        </tr>
      </thead>
      <tbody>
        {bodyInfo.map((info, i) => {
          return (
            <tr key={i}>
              <td
                className={ClassNames("center-cell", { "title-cell": i === 0 })}
              >
                {isFunction(info.title)
                  ? info.title(props.symbolDetail)
                  : info.title}
              </td>
              {props.data.map((d: APIResponse.Res, j: number) => (
                <React.Fragment key={j}>{info.component(d)}</React.Fragment>
              ))}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

const formatKessanKi = (ki: number) => {
  switch (ki) {
    case 1:
      return "1Q"
    case 2:
      return "中間"
    case 3:
      return "3Q(累計)"
    case 4:
      return "本決算(通期)"
  }
}

const formatRentan = (flag: number) => {
  switch (flag) {
    case 1:
      return "単"
    case 2:
      return "連"
    default:
      return "-"
  }
}

const settlementTableInfo: HistoryTableInfo = [
  {
    title: "決算期",
    component: (data: APIResponse.Res) => {
      const isYosou = get(data, "YosouKubun")
      return isYosou ? (
        <span className="center-cell">{`予想(${get(
          data,
          "KessanTsuki"
        )}月)`}</span>
      ) : (
        <span className="center-cell">{`${get(data, "KessanNen")}/${get(
          data,
          "KessanTsuki"
        )}`}</span>
      )
    },
  },
  {
    title: "発表日",
    component: (data: APIResponse.Res) => {
      return (
        <td className="center-cell">{`${Formatter.date(
          get(data, "HappyouBi"),
          "YYYY/MM/DD"
        )}`}</td>
      )
    },
  },
  {
    title: "連・単",
    component: (data: APIResponse.Res) => {
      return (
        <td className="center-cell">{`${formatRentan(
          get(data, "RenTanFlag")
        )}`}</td>
      )
    },
  },
  {
    title: "売上高",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "Uriagedaka")
        )}`}</td>
      )
    },
  },
  {
    title: "営業利益",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "EigyouRieki")
        )}`}</td>
      )
    },
  },
  {
    title: (data: APIResponse.Res) =>
      judgingDomesticEquity(data) ? "経常利益" : "税引前当期利益",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "KeijouRieki")
        )}`}</td>
      )
    },
  },
  {
    title: "当期利益",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "ToukiRieki")
        )}`}</td>
      )
    },
  },
  {
    title: "EPS",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "EPS"),
          2
        )}`}</td>
      )
    },
  },
  {
    title: "配当",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "DPS"),
          2
        )}`}</td>
      )
    },
  },
  {
    title: "ROE(%)",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "ROE"),
          2
        )}`}</td>
      )
    },
  },
  {
    title: "ROA(%)",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "ROA"),
          2
        )}`}</td>
      )
    },
  },
]

const settlementTableInfoSp = {
  header: [
    () => {
      return <div className="title-cell">決算期:</div>
    },
    (data: object) => {
      return (
        <span className="center-cell">{`${get(data, "KessanNen")}/${get(
          data,
          "KessanTsuki"
        )}`}</span>
      )
    },
  ],
  body: [
    {
      title: "発表日",
      value: (data: object) => {
        return (
          <span className="center-cell">{`${Formatter.date(
            get(data, "HappyouBi"),
            "YYYY/MM/DD"
          )}`}</span>
        )
      },
    },
    {
      title: "連・単",
      value: (data: object) => {
        return (
          <span className="center-cell">{`${formatRentan(
            get(data, "RenTanFlag")
          )}`}</span>
        )
      },
    },
    {
      title: "売上高",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "Uriagedaka")
          )}`}</span>
        )
      },
    },
    {
      title: "営業利益",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "EigyouRieki")
          )}`}</span>
        )
      },
    },
    {
      title: (data: APIResponse.Res) =>
        judgingDomesticEquity(data) ? "経常利益" : "税引前当期利益",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "KeijouRieki")
          )}`}</span>
        )
      },
    },
    {
      title: "当期利益",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "ToukiRieki")
          )}`}</span>
        )
      },
    },
    {
      title: "EPS",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "EPS"),
            2
          )}`}</span>
        )
      },
    },
    {
      title: "配当",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "DPS"),
            2
          )}`}</span>
        )
      },
    },
    {
      title: "ROE(%)",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "ROE"),
            2
          )}`}</span>
        )
      },
    },
    {
      title: "ROA(%)",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "ROA"),
            2
          )}`}</span>
        )
      },
    },
  ],
}

const financeTableInfo: HistoryTableInfo = [
  {
    title: "決算期",
    component: (data: APIResponse.Res) => {
      return (
        <span className="center-cell">{`${get(data, "KessanNen")}/${get(
          data,
          "KessanTsuki"
        )}`}</span>
      )
    },
  },
  {
    title: "発表日",
    component: (data: APIResponse.Res) => {
      return (
        <td className="center-cell">{`${Formatter.date(
          get(data, "HappyouBi"),
          "YYYY/MM/DD"
        )}`}</td>
      )
    },
  },
  {
    title: "連・単",
    component: (data: APIResponse.Res) => {
      return (
        <td className="center-cell">{`${formatRentan(
          get(data, "RenTanFlag")
        )}`}</td>
      )
    },
  },
  {
    title: "総資産",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "SouShisan")
        )}`}</td>
      )
    },
  },
  {
    title: "純資産",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "JunShisan")
        )}`}</td>
      )
    },
  },
  {
    title: (data: APIResponse.Res) =>
      get(data, "SecurityType") === SECURITY_TYPE.Equity.id
        ? "自己資本"
        : "株主資本",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "KabunushiShihon")
        )}`}</td>
      )
    },
  },
  {
    title: "資本余剰金",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "ShihonJouyoKin")
        )}`}</td>
      )
    },
  },
  {
    title: "利益余剰金",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "RiekiJouyoKin")
        )}`}</td>
      )
    },
  },
  {
    title: "金融収支",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "KinyuuSHuushi")
        )}`}</td>
      )
    },
  },
  {
    title: (data: APIResponse.Res) =>
      judgingDomesticEquity(data) ? "有利子負債" : "長期有利子負債",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "ChoukiYuurishiHusai")
        )}`}</td>
      )
    },
  },
  {
    title: "短期有利子負債",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "TankiYuurishiHusai")
        )}`}</td>
      )
    },
  },
  {
    title: "設備投資",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "SetsubiToushi")
        )}`}</td>
      )
    },
  },
  {
    title: "減価償却費",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "GenkaShoukyakuHi")
        )}`}</td>
      )
    },
  },
  {
    title: "研究開発費",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "KenkyuuKaihatsuHi")
        )}`}</td>
      )
    },
  },
  {
    title: "1株当り純資産",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "BPS"),
          2
        )}`}</td>
      )
    },
  },
]

const financeTableInfoSp = {
  header: [
    () => {
      return <div className="title-cell">決算期:</div>
    },
    (data: object) => {
      return (
        <span className="center-cell">{`${get(data, "KessanNen")}/${get(
          data,
          "KessanTsuki"
        )}`}</span>
      )
    },
  ],
  body: [
    {
      title: "発表日",
      value: (data: object) => {
        return (
          <span className="center-cell">{`${Formatter.date(
            get(data, "HappyouBi"),
            "YYYY/MM/DD"
          )}`}</span>
        )
      },
    },
    {
      title: "連・単",
      value: (data: object) => {
        return (
          <span className="center-cell">{`${formatRentan(
            get(data, "RenTanFlag")
          )}`}</span>
        )
      },
    },
    {
      title: "総資産",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "SouShisan")
          )}`}</span>
        )
      },
    },
    {
      title: "純資産",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "JunShisan")
          )}`}</span>
        )
      },
    },
    {
      title: (data: APIResponse.Res) =>
        judgingDomesticEquity(data) ? "自己資本" : "株主資本",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "KabunushiShihon")
          )}`}</span>
        )
      },
    },
    {
      title: "資本余剰金",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "ShihonJouyoKin")
          )}`}</span>
        )
      },
    },
    {
      title: "利益余剰金",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "RiekiJouyoKin")
          )}`}</span>
        )
      },
    },
    {
      title: "金融収支",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "KinyuuSHuushi")
          )}`}</span>
        )
      },
    },
    {
      title: (data: object) =>
        judgingDomesticEquity(data) ? "有利子負債" : "長期有利子負債",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "ChoukiYuurishiHusai")
          )}`}</span>
        )
      },
    },
    {
      title: "短期有利子負債",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "TankiYuurishiHusai")
          )}`}</span>
        )
      },
    },
    {
      title: "設備投資",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "SetsubiToushi")
          )}`}</span>
        )
      },
    },
    {
      title: "減価償却費",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "GenkaShoukyakuHi")
          )}`}</span>
        )
      },
    },
    {
      title: "研究開発費",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "KenkyuuKaihatsuHi")
          )}`}</span>
        )
      },
    },
    {
      title: "1株当り純資産",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "BPS"),
            2
          )}`}</span>
        )
      },
    },
  ],
}

const flowTableInfo: HistoryTableInfo = [
  {
    title: "決算期",
    component: (data: APIResponse.Res) => {
      return (
        <span className="center-cell">{`${get(data, "KessanNen")}/${get(
          data,
          "KessanTsuki"
        )}`}</span>
      )
    },
  },
  {
    title: "営業CF",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "EigyouCF")
        )}`}</td>
      )
    },
  },
  {
    title: "投資CF",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "ToushiCF")
        )}`}</td>
      )
    },
  },
  {
    title: "財務CF",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "ZaimuCF")
        )}`}</td>
      )
    },
  },
  {
    title: "フリーCF",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "FreeCF")
        )}`}</td>
      )
    },
  },
  {
    title: "現金同等物",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "GenkinDoutouButsu")
        )}`}</td>
      )
    },
  },
]

const flowTableInfoSp = {
  header: [
    () => {
      return <div className="title-cell">決算期:</div>
    },
    (data: object) => {
      return (
        <span className="center-cell">{`${get(data, "KessanNen")}/${get(
          data,
          "KessanTsuki"
        )}`}</span>
      )
    },
  ],
  body: [
    {
      title: "営業CF",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "EigyouCF")
          )}`}</span>
        )
      },
    },
    {
      title: "投資CF",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "ToushiCF")
          )}`}</span>
        )
      },
    },
    {
      title: "財務CF",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "ZaimuCF")
          )}`}</span>
        )
      },
    },
    {
      title: "フリーCF",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "FreeCF")
          )}`}</span>
        )
      },
    },
    {
      title: "現金同等物",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "GenkinDoutouButsu")
          )}`}</span>
        )
      },
    },
  ],
}

const quarterTableInfo: HistoryTableInfo = [
  {
    title: "決算期",
    component: (data: APIResponse.Res) => {
      return (
        <span className="center-cell">{`${formatKessanKi(
          get(data, "Ki", null)
        )}`}</span>
      )
    },
  },
  {
    title: "更新日",
    component: (data: APIResponse.Res) => {
      return (
        <td className="center-cell">{`${Formatter.date(
          get(data, "HappyouBi"),
          "YYYY/MM/DD"
        )}`}</td>
      )
    },
  },
  {
    title: "連・単",
    component: (data: APIResponse.Res) => {
      return (
        <td className="center-cell">{`${formatRentan(
          get(data, "RenTanFlag")
        )}`}</td>
      )
    },
  },
  {
    title: "売上高",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "Uriagedaka")
        )}`}</td>
      )
    },
  },
  {
    title: "営業利益",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "EigyouRieki")
        )}`}</td>
      )
    },
  },
  {
    title: "経常利益",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "KeijouRieki")
        )}`}</td>
      )
    },
  },
  {
    title: "当期利益",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "ToukiRieki")
        )}`}</td>
      )
    },
  },
  {
    title: "EPS",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "EPS"),
          2
        )}`}</td>
      )
    },
  },
  {
    title: "配当",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "DPS"),
          2
        )}`}</td>
      )
    },
  },
]

const quarterTableInfoSp = {
  header: [
    () => {
      return <div className="title-cell">決算期:</div>
    },
    (data: object) => {
      return (
        <span className="date-cell">{`${formatKessanKi(
          get(data, "Ki", null)
        )}`}</span>
      )
    },
  ],
  body: [
    {
      title: "更新日",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.date(
            get(data, "HappyouBi"),
            "YYYY/MM/DD"
          )}`}</span>
        )
      },
    },
    {
      title: "連・単",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${formatRentan(
            get(data, "RenTanFlag")
          )}`}</span>
        )
      },
    },
    {
      title: "売上高",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "Uriagedaka")
          )}`}</span>
        )
      },
    },
    {
      title: "営業利益",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "EigyouRieki")
          )}`}</span>
        )
      },
    },
    {
      title: "経常利益",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "KeijouRieki")
          )}`}</span>
        )
      },
    },
    {
      title: "当期利益",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "ToukiRieki")
          )}`}</span>
        )
      },
    },
    {
      title: "EPS",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "EPS"),
            2
          )}`}</span>
        )
      },
    },
    {
      title: "配当",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "DPS"),
            2
          )}`}</span>
        )
      },
    },
  ],
}

const reitTableInfo: HistoryTableInfo = [
  {
    title: "決算期",
    component: (data: APIResponse.Res) => {
      const kessan = !!get(data, "KessanNen", null)
        ? get(data, "KessanNen", null) + "/" + get(data, "KessanTsuki", null)
        : ""
      return <span className="center-cell">{kessan}</span>
    },
  },
  {
    title: "更新日",
    component: (data: APIResponse.Res) => {
      return (
        <td className="center-cell">{`${Formatter.date(
          get(data, "HappyouBi"),
          "YYYY/MM/DD"
        )}`}</td>
      )
    },
  },
  {
    title: "連・単",
    component: (data: APIResponse.Res) => {
      return (
        <td className="center-cell">{`${formatRentan(
          get(data, "RenTanFlag")
        )}`}</td>
      )
    },
  },
  {
    title: "売上高",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "Uriagedaka")
        )}`}</td>
      )
    },
  },
  {
    title: "営業利益",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "EigyouRieki")
        )}`}</td>
      )
    },
  },
  {
    title: "経常利益",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "KeijouRieki")
        )}`}</td>
      )
    },
  },
  {
    title: "当期利益",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "ToukiRieki")
        )}`}</td>
      )
    },
  },
  {
    title: "EPS",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "EPS"),
          2
        )}`}</td>
      )
    },
  },
  {
    title: "配当",
    component: (data: APIResponse.Res) => {
      return (
        <td className="number-cell">{`${Formatter.number(
          get(data, "DPS"),
          2
        )}`}</td>
      )
    },
  },
]

const reitTableInfoSp = {
  header: [
    () => {
      return <div className="title-cell">決算期:</div>
    },
    (data: object) => {
      const kessan = !!get(data, "KessanNen", null)
        ? get(data, "KessanNen", null) + "/" + get(data, "KessanTsuki", null)
        : ""
      return <span className="date-cell">{kessan}</span>
    },
  ],
  body: [
    {
      title: "更新日",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.date(
            get(data, "HappyouBi"),
            "YYYY/MM/DD"
          )}`}</span>
        )
      },
    },
    {
      title: "連・単",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${formatRentan(
            get(data, "RenTanFlag")
          )}`}</span>
        )
      },
    },
    {
      title: "売上高",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "Uriagedaka")
          )}`}</span>
        )
      },
    },
    {
      title: "営業利益",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "EigyouRieki")
          )}`}</span>
        )
      },
    },
    {
      title: "経常利益",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "KeijouRieki")
          )}`}</span>
        )
      },
    },
    {
      title: "当期利益",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "ToukiRieki")
          )}`}</span>
        )
      },
    },
    {
      title: "EPS",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "EPS"),
            2
          )}`}</span>
        )
      },
    },
    {
      title: "配当",
      value: (data: object) => {
        return (
          <span className="number-cell">{`${Formatter.number(
            get(data, "DPS"),
            2
          )}`}</span>
        )
      },
    },
  ],
}

const contentLabel = [
  { name: "業績", id: 0 },
  { name: "CF", id: 1 },
  { name: "四半期", id: 2 },
  { name: "配当", id: 3 },
  { name: "指標", id: 4 },
]

const performanceLabel = [{ name: "業績推移", id: 0 }, { name: "財務", id: 1 }]

const quarterLabel = [{ name: "前期", id: 0 }, { name: "今期", id: 1 }]

const Settlement: React.FC<SettlementProps> = props => {
  const settlementBarItems = !props.type
    ? [
        { name: "営業利益", value: "EigyouRieki" },
        { name: "経常利益", value: "KeijouRieki" },
        { name: "当期利益", value: "ToukiRieki" },
      ]
    : [
        { name: "総資産", value: "SouShisan" },
        {
          name: judgingDomesticEquity(props.symbolDetail)
            ? "自己資本"
            : "株主資本",
          value: "KabunushiShihon",
        },
        { name: "1株当たり純資産", value: "BPS" },
      ]

  const settlementData: Array<APIResponse.Res> = orderBy(
    get(props.settlement, "ResultList", []),
    ["Nendo"],
    ["asc"]
  )

  return (
    <div className="title-table-container">
      <div className="title-border">
        <span className="title-text">業績詳細</span>
        <div className="filter-control-item">
          <Labels
            className="btn-segmented-control btn-thin liquid-control"
            labelList={performanceLabel}
            checked={props.type}
            onChange={(id: number) => props.setType(id)}
          />
        </div>
      </div>
      {!props.isSp ? (
        <PerformanceHistoryBar
          data={settlementData}
          list={settlementBarItems}
        />
      ) : null}
      {!props.isSp ? (
        !isEmpty(settlementData) ? (
          <HistoryTable
            cls="table-striped settlement"
            data={settlementData}
            symbolDetail={props.symbolDetail}
            info={
              !props.type
                ? settlementTableInfo
                : judgingDomesticEquity(props.symbolDetail)
                ? //国内株は短期と長期ではなく有利子負債として表示する（パラメータは短期か長期どちらかのを使用すればOK）
                  filter(
                    financeTableInfo,
                    items =>
                      items.title !== "短期有利子負債" &&
                      items.title !== "資本余剰金" &&
                      items.title !== "金融収支"
                  )
                : financeTableInfo
            }
          />
        ) : (
          <div className="aberrant">表示データ無し</div>
        )
      ) : (
        <SpDetailTable
          headerList={
            !props.type
              ? settlementTableInfoSp.header
              : financeTableInfoSp.header
          }
          bodyList={
            !props.type
              ? settlementTableInfoSp.body
              : judgingDomesticEquity(props.symbolDetail)
              ? //国内株は短期と長期ではなく有利子負債として表示する（パラメータは短期か長期どちらかのを使用すればOK）
                filter(
                  financeTableInfoSp.body,
                  items =>
                    items.title !== "短期有利子負債" &&
                    items.title !== "資本余剰金" &&
                    items.title !== "金融収支"
                )
              : financeTableInfoSp.body
          }
          data={settlementData.reverse()} //最新から見せたいので.reverseで逆順に
        />
      )}
    </div>
  )
}

const CashFlow: React.FC<CashFlowProps> = props => {
  const flowData: Array<APIResponse.Res> = orderBy(
    get(props.flow, "ResultList", []),
    ["Nendo"],
    ["asc"]
  )
  const flowBarItems = [
    { name: "営業CF", value: "EigyouCF" },
    { name: "フリーCF", value: "FreeCF" },
    { name: "現物同等物", value: "GenkinDoutouButsu" },
  ]
  return (
    <div className="title-table-container">
      <div className="title-border">キャッシュフロー</div>
      {!props.isSp ? (
        <PerformanceHistoryBar data={flowData} list={flowBarItems} />
      ) : null}
      {!props.isSp ? (
        !isEmpty(flowData) ? (
          <HistoryTable
            cls="table-striped"
            data={flowData}
            info={flowTableInfo}
          />
        ) : (
          <div className="aberrant">表示データ無し</div>
        )
      ) : (
        <SpDetailTable
          headerList={flowTableInfoSp.header}
          bodyList={flowTableInfoSp.body}
          data={flowData.reverse()} //最新から見せたいので.reverseで逆順に
        />
      )}
    </div>
  )
}

const Quarter: React.FC<QuarterProps> = props => {
  const defaultData: Array<object> = [
    { Ki: 1 },
    { Ki: 2 },
    { Ki: 3 },
    { Ki: 4 },
  ]
  const resultList: Array<APIResponse.Res> = get(props.data, "ResultList", [])
  const recentData = head(resultList)
  let thisYears = defaultData
  let lastYears = defaultData

  const sortByYear = (dataList: Array<APIResponse.Res>) => {
    return orderBy(dataList, data => get(data, "Nendo"), "asc")
  }
  const sortByKi = (dataList: Array<APIResponse.Res>) => {
    return orderBy(dataList, data => get(data, "Ki"), "asc")
  }

  const eachData = (
    dataList: Array<APIResponse.Res>,
    target: Array<object>
  ) => {
    forEach(dataList, (data: object) => {
      const _ki = get(data, "Ki")
      target[_ki - 1] = data
    })
  }

  //データが10件以上、もしくは9件で最初のデータが予想でなければデータ異常とする
  const aberrantDataFlag =
    resultList.length >= 10 ||
    (resultList.length === 9 && !get(recentData, "YosouKubun"))

  //9件以上の場合、最新のデータが予想であれば最新のデータ以外8件を表示する
  if (resultList.length === 9 && !!get(recentData, "YosouKubun")) {
    const _result = chunk(drop(resultList), 4)
    thisYears = sortByKi(_result[0])
    lastYears = sortByKi(_result[1])

    //8件であれば先頭4つが今期、後半4つが前期のデータ
  } else if (resultList.length === 8) {
    thisYears = sortByKi(take(resultList, 4))
    lastYears = sortByKi(takeRight(resultList, 4))

    //8件以下、5件以上であれば、年度でソートし古いデータ4件が前期
    //新しいデータはそれぞれKiに当てはめていく
  } else if (resultList.length >= 5) {
    const _thisYears = cloneDeep(defaultData)
    const _result = chunk(sortByYear(resultList), 4)
    eachData(_result[1], _thisYears)
    thisYears = _thisYears
    lastYears = sortByKi(_result[0])

    //4件以下であればすべて今期のデータ（0件はdefaultDataを返すので1件以上あるのが前提）
  } else if (resultList.length <= 4 && resultList.length > 0) {
    const _thisYearsOnly = cloneDeep(defaultData)
    eachData(resultList, _thisYearsOnly)
    thisYears = _thisYearsOnly
  }

  return (
    <div className="title-table-container">
      <div className="title-border">
        <span className="title-text">四半期</span>
        <div className="filter-control-item">
          <Labels
            className="btn-segmented-control btn-thin liquid-control"
            labelList={quarterLabel}
            checked={props.type}
            onChange={(id: number) => props.setType(id)}
          />
        </div>
      </div>
      {!aberrantDataFlag ? (
        <>
          {!props.isSp ? (
            <HistoryTable
              cls="table-striped"
              data={!props.type ? lastYears : thisYears}
              info={quarterTableInfo}
            />
          ) : (
            <SpDetailTable
              headerList={quarterTableInfoSp.header}
              bodyList={quarterTableInfoSp.body}
              data={!props.type ? lastYears : thisYears}
            />
          )}
        </>
      ) : (
        <div className="aberrant">
          取得したデータが異常なため表示できません。
        </div>
      )}
    </div>
  )
}

const Reit: React.FC<ReitProps> = props => {
  const list: APIResponse.Res = get(props.data, "ResultList", [])
  if (isEmpty(list)) return <></>
  const arr: Array<object> = []
  // REIT専用

  const sortList = orderBy(list, ["KessanNen", "KessanTsuki"])

  if (sortList.length < 4) {
    // 不足している数、{}で埋めた配列を作成後、元の配列にマージ。
    const fillList = new Array(4 - sortList.length)
    fill(fillList, {})
    sortList.push(...fillList)
  }

  return (
    <div className="title-table-container">
      <div className="title-border">
        <span className="title-text">四半期</span>
      </div>
      {!props.isSp ? (
        <HistoryTable
          cls="table-striped"
          data={sortList}
          info={reitTableInfo}
        />
      ) : (
        <SpDetailTable
          headerList={reitTableInfoSp.header}
          bodyList={reitTableInfoSp.body}
          data={sortList}
        />
      )}
    </div>
  )
}

const Dividend: React.FC<DividendProps> = props => {
  const dividendBarItems = [
    { name: "配当", value: "DPS" },
    { name: "配当性向", value: "HaitouSeikou" },
    { name: "純資産配当率", value: "HaitouJunShisanHiritsu" },
  ]
  const currencyCode = get(props.symbolDetail, "CurrencyCode")
  const securityType = get(props.symbolDetail, "SecurityType")
  const historyData = get(props.history, "ResultList", [])
  props.dividend.CurrencyCode = currencyCode
  props.dividend.SecurityType = securityType
  forEach(historyData, data => {
    data.CurrencyCode = currencyCode
    data.SecurityType = securityType
  })
  const historyBarData = cloneDeep(historyData).reverse()

  return (
    <div className="title-table-container">
      <div className="title-border">配当詳細/配当</div>
      {!props.isSp ? (
        <PerformanceHistoryBar data={historyBarData} list={dividendBarItems} />
      ) : null}
      <InfoTable
        list={infoTableColumns.symbolDetail.performance.dividendDetail}
        data={props.dividend}
        className="table-striped table-index-detail"
        wrapperCls={!props.isSp ? "index-detail-table-row dividend" : ""}
      />
      <BaseTable
        className="table-striped dividend"
        list={tableColumns.symbolDetail.performance.dividend}
        data={historyData}
      />
    </div>
  )
}

const Indexes: React.FC<IndexesProps> = props => {
  const currencyCode = get(props.symbolDetail, "CurrencyCode")
  const securityType = get(props.symbolDetail, "SecurityType")
  const isForeign = securityType !== SECURITY_TYPE.Equity.id
  props.indexes.CurrencyCode = currencyCode
  const indexesBarItem = [
    {
      title: "割安性",
      list: [
        { name: "PER(実)", value: "R_PER", compare: "AVERAGE_R_PER" },
        { name: "PER(予)", value: "E_PER", compare: "AVERAGE_E_PER" },
        { name: "PBR(実)", value: "R_PBR", compare: "AVERAGE_R_PBR" },
      ],
    },
    {
      title: "安定性",
      list: [
        { name: "BPS(実)", value: "R_BPS", compare: "AVERAGE_R_BPS" },
        {
          name: "自己資本比率",
          value: "JikoShihonHiritsu",
          compare: "AVERAGE_JikoShihonHiritsu",
        },
        {
          name: "流動比率",
          value: "RyuudouHiritsu",
          compare: "AVERAGE_RyuudouHiritsu",
        },
      ],
    },
    {
      title: "収益性",
      list: [
        { name: "ROE(実)", value: "R_ROE", compare: "AVERAGE_R_ROE" },
        { name: "ROA(実)", value: "R_ROA", compare: "AVERAGE_R_ROA" },
        {
          name: "売上高営業利益率",
          value: "UriagedakaEigyouRiekiRitsu",
          compare: "AVERAGE_UriagedakaEigyouRiekiRitsu",
        },
      ],
    },
    {
      title: "成長性",
      list: [
        {
          name: "EPS5年成長率",
          value: "EPS5NenSeichouRitsu",
          compare: "AVERAGE_EPS5NenSeichouRitsu",
        },
        {
          name: "EPS対前年比伸び率",
          value: "EPSTaiZennenHiNobiRitsu",
          compare: "AVERAGE_EPSTaiZennenHiNobiRitsu",
        },
        {
          name: "売上高 - 5年成長率",
          value: "Uriagedaka5NenSeichouRitsu",
          compare: "AVERAGE_Uriagedaka5NenSeichouRitsu",
        },
      ],
    },
  ]
  return (
    <>
      {!props.isSp
        ? map(indexesBarItem, (items, key) => {
            return (
              <React.Fragment key={key}>
                <div className="title-border">{get(items, "title")}</div>
                <PerformanceCompareBar
                  data={props.indexes}
                  list={get(items, "list")}
                  className="performance-compare-bar"
                  noneLabelDiagonal={true}
                />
              </React.Fragment>
            )
          })
        : null}
      <div
        className={ClassNames("title-table-container", {
          "split-division": !props.isSp,
        })}
      >
        {tableList.map((list, i) => {
          return (
            <div key={i} className="panel-splits panel-split-price-col">
              {list.map((info, j) => {
                //  参照渡しをしていると、setで上書きしてしまうので、別のメモリにコピーする。
                let tableList = {
                  ...get(infoTableColumns.symbolDetail.performance, info.table),
                }
                // 日株の場合、PBR(予)カラムを削除。
                tableList =
                  info.table === "inexpensive" &&
                  get(props.symbolDetail, "SecurityType") ===
                    SECURITY_TYPE.Equity.id
                    ? set(tableList, "list", [
                        filter(
                          get(tableList, ["list", 0], []),
                          items => items.title !== "PBR(予)"
                        ),
                      ])
                    : tableList

                // 日株の場合、BPS(予)カラムを削除。
                tableList =
                  info.table === "safety" &&
                  get(props.symbolDetail, "SecurityType") ===
                    SECURITY_TYPE.Equity.id
                    ? set(tableList, "list", [
                        filter(
                          get(tableList, ["list", 0], []),
                          items => items.title !== "BPS(予)"
                        ),
                      ])
                    : tableList

                return (
                  <div
                    key={j}
                    className={ClassNames("title-table-container", {
                      foreign: isForeign,
                    })}
                  >
                    <div className="title-border">{info.name}</div>
                    <InfoTable
                      key={i}
                      list={tableList}
                      data={props.indexes}
                      className="table-striped table-index-detail"
                      wrapperCls="index-detail-table-row"
                    />
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>
    </>
  )
}

export const Performance: React.FC<PerformanceProps> = props => {
  const symbol = useMemo(() => {
    return get(props.symbolDetail, "Symbol", {})
  }, [props.symbolDetail])
  const param: { Symbol: string; SecurityType: string } = useMemo(() => {
    return pick(symbol, ["Symbol", "SecurityType"])
  }, [symbol])
  const isRefresh = useSelector(
    (state: { symbolDetail: { refresh: boolean } }) =>
      state.symbolDetail.refresh
  )
  const [
    data,
    type,
    setType,
    settlementType,
    setSettlementType,
    quarterType,
    setQuarterType,
  ] = useSettledAccountReport(param, isRefresh)

  const isSp = checkIsSp()
  const isReit: boolean =
    get(symbol, "ExchangeDivisions.0.Division", "") === "0119"
  const components = [
    {
      id: 0,
      component: (
        <Settlement
          type={settlementType}
          setType={setSettlementType}
          settlement={data.settlement}
          isSp={isSp}
          symbolDetail={symbol}
        />
      ),
    },
    { id: 1, component: <CashFlow flow={data.flow} isSp={isSp} /> },
    {
      id: 2,
      component: !isReit ? (
        <Quarter
          data={data.quarter}
          type={quarterType}
          setType={(id: number) => setQuarterType(id)}
          isSp={isSp}
        />
      ) : (
        <Reit
          data={data.quarter}
          type={quarterType}
          setType={(id: number) => setQuarterType(id)}
          isSp={isSp}
        />
      ),
    },
    {
      id: 3,
      component: (
        <Dividend
          history={data.history}
          dividend={data.dividend}
          isSp={isSp}
          symbolDetail={symbol}
        />
      ),
    },
    {
      id: 4,
      component: (
        <Indexes indexes={data.indexes} isSp={isSp} symbolDetail={symbol} />
      ),
    },
  ]
  const displayComponent = components.find(lst => lst.id === type)
  return (
    <div className="panel-division panel-performance">
      {get(symbol, "SecurityType") === SECURITY_TYPE.Equity.id && (
        <NewsOne symbolDetail={symbol} />
      )}
      <div className="title-table-container">
        <div className="filter-control-item">
          <Labels
            className="btn-segmented-control btn-thin liquid-control"
            labelList={contentLabel}
            checked={type}
            onChange={(id: number) => setType(id)}
          />
        </div>
      </div>
      {displayComponent.component}
    </div>
  )
}
